import { z } from "zod";

// TES-332 [J-retention] — data-lifecycle DESIGN layer.
//
// This file is the DESIGN half of the retention epic: the category taxonomy +
// the retention RULES (as DATA), and the small pure helpers that classify a
// row into a category. It deliberately contains NO deletion / anonymisation
// JOB — the actual timers that erase or anonymise data are LEGAL-GATED and
// deferred pending legal sign-off (GDPR + Swedish PDL). See
// docs/data-retention-classification.md for the model-by-model mapping.
//
// The four categories, medical-legal weakest-retention-last:
//
//   journalnara      Medical-legal record. Swedish PDL (Patientdatalagen)
//                    requires a patientjournal be kept ≥10 years after the
//                    last entry. NEVER auto-deletable. GDPR Art. 17 erasure
//                    is satisfied by ANONYMISING the person (scrubbing PII)
//                    while PRESERVING the clinical record + audit chains —
//                    it is never a hard delete of these rows.
//   vardrelevant     Care-relevant, journal-adjacent. Retained alongside the
//                    journal (journal-adjacent retention) because it informs
//                    or contextualises care, but is not itself the legal
//                    record. Not auto-deletable while the journal is retained.
//   teknisk          Technical / operational. Short retention; anonymisable
//                    or prunable once its operational purpose is served.
//   avtal_bokforing  Contract / accounting. The Swedish Accounting Act requires
//                    accounting records be kept 7 years after the end of the
//                    financial year. Retention is legal, not clinical.

export const DATA_CATEGORIES = [
  "journalnara",
  "vardrelevant",
  "teknisk",
  "avtal_bokforing",
] as const;
export const dataCategorySchema = z.enum(DATA_CATEGORIES);
export type DataCategory = (typeof DATA_CATEGORIES)[number];

// The subset of categories that a retention JOB may NEVER hard-delete or
// scrub the clinical columns of. The GDPR anonymise path asserts against this
// (apps/api patient.gdpr.service.ts) so a future edit that tries to delete a
// `journalnara` row through the erasure flow fails loudly instead of silently
// destroying a medical-legal record.
export const NON_DELETABLE_CATEGORIES = [
  "journalnara",
] as const satisfies readonly DataCategory[];

export function isNonDeletableCategory(category: DataCategory): boolean {
  return (NON_DELETABLE_CATEGORIES as readonly DataCategory[]).includes(
    category,
  );
}

// A retention RULE per category — DATA ONLY. `basis` is the legal/clinical
// grounding; `rule` is the human-readable retention period; `autoDeletable`
// says whether a (future, legal-signed-off) job is even ALLOWED to erase the
// row automatically. `minRetention` is a coarse machine-readable floor for a
// future job to reason about — NOT a schedule, and NOT wired to any timer.
export interface RetentionRule {
  category: DataCategory;
  basis: string;
  rule: string;
  autoDeletable: boolean;
  // Coarse floor, for a future job's guard math only. null = no auto-delete.
  minRetention: "10y" | "7y" | "60d" | "30d" | null;
}

export const RETENTION_PERIODS: Record<DataCategory, RetentionRule> = {
  journalnara: {
    category: "journalnara",
    basis: "PDL (Patientdatalagen) — patientjournal",
    rule: "≥10y after last journal entry (PDL); GDPR erasure = anonymise person, retain clinical record",
    autoDeletable: false,
    minRetention: "10y",
  },
  vardrelevant: {
    category: "vardrelevant",
    basis: "Journal-adjacent — informs/contextualises care",
    rule: "Retained alongside the journal (journal-adjacent); not auto-deleted while the journal is retained",
    autoDeletable: false,
    minRetention: "10y",
  },
  teknisk: {
    category: "teknisk",
    basis: "Operational — no clinical/legal record value",
    rule: "Short retention; prune/anonymise once operational purpose served (e.g. 30–60d)",
    autoDeletable: true,
    minRetention: "60d",
  },
  avtal_bokforing: {
    category: "avtal_bokforing",
    basis: "Bokföringslagen (SE accounting) + contract",
    rule: "7y after end of financial year (Bokföringslagen)",
    autoDeletable: false,
    minRetention: "7y",
  },
};

// DomainEvent (TES-280) carries its own retention discriminator `category`
// with values 'care_relevant' | 'technical'. Map those onto the taxonomy so
// the event log inherits a single retention vocabulary:
//   care_relevant → vardrelevant (journal-adjacent)
//   technical     → teknisk       (short retention / prunable)
export const DOMAIN_EVENT_CATEGORIES = ["care_relevant", "technical"] as const;
export const domainEventCategorySchema = z.enum(DOMAIN_EVENT_CATEGORIES);
export type DomainEventCategory = (typeof DOMAIN_EVENT_CATEGORIES)[number];

const DOMAIN_EVENT_CATEGORY_TO_DATA_CATEGORY: Record<
  DomainEventCategory,
  DataCategory
> = {
  care_relevant: "vardrelevant",
  technical: "teknisk",
};

// Total over DOMAIN_EVENT_CATEGORIES. Falls back to `teknisk` (the
// shortest-retention, safest-to-prune bucket) for any unknown string so a new
// event category never accidentally lands in a NON_DELETABLE bucket.
export function retentionCategoryForEvent(category: string): DataCategory {
  return (
    DOMAIN_EVENT_CATEGORY_TO_DATA_CATEGORY[category as DomainEventCategory] ??
    "teknisk"
  );
}
